import React from "react";
import { GridItem } from "../common/GridItem";
import { Sign } from "./Sign";
import { Field, makeStyles, Text, tokens } from "@fluentui/react-components";
import { UseFormReturn } from "react-hook-form";
import { ISignForm, Reflex, SignColor } from "../../interfaces/ISignForm";
import { getPriceBreakdown } from "../../helper/pricing";
import { useTranslation } from "react-i18next";
type SignSummaryProps = {
  signForm: UseFormReturn<ISignForm, any, ISignForm>;
  step: number;
};
const useStyles = makeStyles({
  previewHeader: {
    marginBottom: tokens.spacingVerticalS,
    "> strong": {
      fontWeight: tokens.fontWeightBold,
      fontSize: tokens.fontSizeBase400,
    },
  },
  signContainer: {
    width: "auto",
    height: "auto",
  },
  summaryContainer: {
    display: "flex",
    flexDirection: "column",
  },
  field: {
    fontWeight: tokens.fontWeightBold,
    marginTop: tokens.spacingVerticalL,
    "> p": {
      margin: `${tokens.spacingVerticalXS} 0`,
    },
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
    margin: `${tokens.spacingVerticalXS} 0`,
  },
  priceRowText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  priceDivider: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
  },
  priceDividerText: {
    fontWeight: tokens.fontWeightBold,
  },
});
export const SignSummary = ({ signForm, step }: SignSummaryProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { watch } = signForm;
  const sign = watch();
  const breakdown = getPriceBreakdown(sign);
  return (
    <>
      <GridItem size="5/12">
        <div>
          <div className={styles.previewHeader}>
            <Text as="strong">{t("label.preview")}</Text>
          </div>
          <div className={styles.signContainer}>
            <Sign sign={watch()} modalPreview />
          </div>
        </div>
      </GridItem>
      <GridItem size="3/12">
        <div className={styles.summaryContainer}>
          <Field
            label={t("label.general")}
            className={styles.field}
            size="medium"
          >
            <div className={styles.priceRow}>
              <Text className={styles.priceRowText}>{t("label.basePrice")}</Text>
              <Text className={styles.priceRowText}>{`${breakdown.basePrice} kr`}</Text>
            </div>
            {sign.reflex === Reflex.HIGH && (
              <div className={styles.priceRow}>
                <Text className={styles.priceRowText}>{`+ ${t("label.reflexSurcharge")}`}</Text>
                <Text className={styles.priceRowText}>{`${breakdown.reflexSurcharge} kr`}</Text>
              </div>
            )}
            {sign.graffitiProtection && (
              <div className={styles.priceRow}>
                <Text className={styles.priceRowText}>{`+ ${t("label.graffitiSurcharge")}`}</Text>
                <Text className={styles.priceRowText}>{`${breakdown.graffitiSurcharge} kr`}</Text>
              </div>
            )}
            <div className={styles.priceDivider}>
              <Text as="strong" className={styles.priceDividerText}>
                {sign.quantity > 1 ? t("label.pricePerSign") : t("label.totalPrice")}
              </Text>
              <Text as="strong" className={styles.priceDividerText}>{`${breakdown.total} kr ${t("unit.exVat")}`}</Text>
            </div>
            {sign.quantity > 1 && (
              <>
                <div className={styles.priceRow}>
                  <Text className={styles.priceRowText}>{t("label.amount")}</Text>
                  <Text className={styles.priceRowText}>{`${sign.quantity} ${t("unit.st")}`}</Text>
                </div>
                <div className={styles.priceDivider}>
                  <Text as="strong" className={styles.priceDividerText}>{t("label.totalPrice")}</Text>
                  <Text as="strong" className={styles.priceDividerText}>{`${sign.totalPrice} kr ${t("unit.exVat")}`}</Text>
                </div>
              </>
            )}
          </Field>
        </div>
      </GridItem>
      <GridItem size="2/12">
        <div className={styles.summaryContainer}>
          <Field
            label={t("label.design")}
            className={styles.field}
            size="medium"
          >
            <Text as="p">{`${t("label.width")} ${sign.width}${t("mm")}`}</Text>
            <Text as="p">{`${t("label.height")} ${sign.height}${t(
              "mm"
            )}`}</Text>
            {/* {step === 2 && (
              <Text as="p">{`${t("label.poleDimension2")} ${
                sign.poleDimension
              }${t("mm")}`}</Text>
            )} */}
            <Text as="p">{`${t("label.color")} ${
              sign.primaryColor === SignColor.BLUE
                ? t("option.blueWhite")
                : t("option.yellowRed")
            }`}</Text>
            {sign?.symbol && (
              <Text as="p">{`${t("label.symbol")} ${sign?.symbol?.name}`}</Text>
            )}
            {sign?.texts?.length > 0 && (
              <Text as="p">{`${t("label.amountOfRows")} ${
                sign?.texts?.length
              }`}</Text>
            )}
            <Text as="p">{`${t("label.material")} ${t(
              "material.extruded"
            )}`}</Text>
          </Field>
        </div>
      </GridItem>
      <GridItem size="2/12">
        <div className={styles.summaryContainer}>
          <Field
            label={t("label.additions")}
            className={styles.field}
            size="medium"
          >
            <Text as="p">{`${t("label.reflex")} ${t(
              `enum.${sign.reflex}`
            )}`}</Text>
            {step === 2 && (
              <Text as="p">{`${t("label.graffitiProtection")} ${
                sign.graffitiProtection ? t("badge.yes") : t("badge.no")
              }`}</Text>
            )}
          </Field>
        </div>
      </GridItem>
      {/* <GridItem size="2/12">
        <div className={styles.summaryContainer}>
          <Field
            label={t("label.additions")}
            className={styles.field}
            size="medium"
          >
            <Text as="p">{`${t("label.reflex")} ${t(
              `enum.${sign.reflex}`
            )}`}</Text>
            {step === 2 && (
              <>
                <Text as="p">{`${t("label.graffitiProtection")} ${
                  sign.graffitiProtection ? t("badge.yes") : t("badge.no")
                }`}</Text>
                <Text as="p">{`${t("label.mount")} ${t(
                  `enum.${sign.mount}`
                )}`}</Text>
                {sign.mount === SignMount.POLE && (
                  <Text as="p">{`${t("label.typeOfMounting")} ${t(
                    `select.${sign.mountingPoint}`
                  )}`}</Text>
                )}
                {step === 2 && (
                  <Text as="p">{`${t("label.amountOfClamps2")} ${
                    sign.mountTotal
                  }${t("st")}`}</Text>
                )}
                <Text as="p">{`${t("label.needsMounting")} ${
                  sign.needsMounting ? t("badge.yes") : t("badge.no")
                }`}</Text>
              </>
            )}
          </Field>
        </div>
      </GridItem> */}
    </>
  );
};
